// "Simon Taylor" → "S.Taylor" — keeps the surname, initials the first name, so
// several players fit in one compact row (a team card, a leaderboard row).
// Single-word names are left as-is. Shared by CompetitionLeaderboard.js and
// TeamLiveScoring.js so both read team rosters in the same style.
export const abbreviateName = (full) => {
  const parts = (full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return full || '';
  return `${parts[0][0]}.${parts[parts.length - 1]}`;
};

// "George Hornsby" → "G. Hornsby": the live boards' fallback for a name too
// wide for its column, in the PDF report's own style (report/resultsTable.js).
// Everything after the forename is kept, so "Jamie de la Cruz" stays a Cruz.
// null for a single-word name — there is nothing to shorten it to.
export const initialledName = (full) => {
  const parts = (full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  return `${parts[0][0]}. ${parts.slice(1).join(' ')}`;
};

export const getHandicapStrokes = (ph, si) => {
  if (ph == null || si == null) return 0;
  if (ph > 0) return Math.floor(ph / 18) + (si <= (ph % 18) ? 1 : 0);
  if (ph < 0) {
    const a = Math.abs(ph);
    return -(Math.floor(a / 18) + (si > (18 - (a % 18)) ? 1 : 0));
  }
  return 0;
};

// A played card's score relative to par, as a signed number (0 = level, which
// callers render as "E"; negative = under par). THE definition of "to par" for
// this app, shared so the scoring screen (LiveScoring), the offline board
// (useLocalLeaderboard) and — via the same shape — the server (scoring.py) can
// never drift apart. A player must not see their score change when they switch
// tab or reconnect.
//
// Stableford measures POINTS against the 2-points-a-hole par pace, because the
// points ARE the score: a hole worse than a nett double bogey scores zero
// however many strokes it actually took, so 18 points on a full round is +18,
// not the (larger) nett-strokes figure. Only stroke play, where every stroke
// counts, measures nett strokes against par.
export const roundToPar = ({ isStrokePlay, holesPlayed, points, nettStrokesToPar }) => {
  if (!holesPlayed) return 0;
  return isStrokePlay ? nettStrokesToPar : (2 * holesPlayed) - points;
};

// Stableford points for one hole — mirrors the server's
// calculate_stableford_points EXACTLY, including the 0 floor and the 6-point
// cap (a nett score 4+ under the hole's adjusted par all score 6). The points
// table is re-derived across several client surfaces (LiveScoring, the offline
// board, the finalise grid, HoleByHoleProgress); import this so none of them
// drifts from the server. par/si are the hole's, ph the player's playing hcp.
export const stablefordPoints = (par, ph, si, gross) => {
  const g = parseInt(gross, 10);
  if (par == null || !g || Number.isNaN(g)) return 0;
  const adjustedPar = par + getHandicapStrokes(ph, si);
  return Math.min(6, Math.max(0, adjustedPar + 2 - g));
};

// The card ONE player is scoring on — their tee's par and stroke index, hole by
// hole. In a mixed-tee competition a fourball can hold four different cards, so
// every surface that derives points, nett or to-par must ask per player rather
// than reading one course-wide array. The server sends `hole_pars` /
// `hole_stroke_indices` with each player in a scoring group; `fallbackHoles`
// (the round's own card) covers a player the server hasn't sent them for and
// any session cached offline before mixed tees existed.
//
// Returns [{hole_number, par, strokeIndex}] ordered by hole, and — importantly —
// returns `fallbackHoles` itself when there is nothing per-player, so the
// single-tee case stays referentially stable for useMemo consumers.
export const playerHoleCard = (player, fallbackHoles) => {
  const pars = player?.hole_pars;
  const sis = player?.hole_stroke_indices;
  if (!Array.isArray(pars) || !pars.length) return fallbackHoles || [];
  return pars.map((par, i) => ({
    hole_number: i + 1,
    par,
    strokeIndex: Array.isArray(sis) ? sis[i] : undefined,
  }));
};

// Do the players in this group play off the same tee? Drives whether a single
// par/SI header row can honestly describe the whole card.
export const groupHasMixedTees = (players) => {
  const tees = new Set((players || []).map(p => p?.tee_id ?? null));
  return tees.size > 1;
};

// Players grouped under the tee they played, preserving the order each tee is
// first seen. THE grouping used by both mixed-tee surfaces — the live scoring
// card and the finalise scorecard grid — so the two can't disagree about which
// players belong to which section, or in what order.
//
// `teeOf(player)` returns that player's tee ({id, name, ...}); the two callers
// source it differently (one from the group payload, one from the round's tee
// assignments), which is the only thing that differs between them.
export const groupPlayersByTee = (players, teeOf) => {
  const order = [];
  const byTee = new Map();
  (players || []).forEach(p => {
    const tee = teeOf(p);
    const key = tee?.id ?? 'none';
    if (!byTee.has(key)) { byTee.set(key, { key, tee, players: [] }); order.push(key); }
    byTee.get(key).players.push(p);
  });
  return order.map(k => byTee.get(k));
};

// The scoring card's rows in render order: players grouped under their tee,
// each section preceded by a header row carrying that tee's identity.
//
// A single par/SI row can only ever describe one tee, so a split group gets one
// header per tee sitting directly above the rows it applies to — the only
// arrangement that stays unambiguous with three or more tees. A single-tee
// group returns plain player rows and renders exactly as it always did.
//
// All three panes of the scoring grid map over this same array, so a section
// occupies one row in each and the columns stay aligned.
export const buildScoringRows = (players) => {
  if (!players?.length) return [];
  if (!groupHasMixedTees(players)) {
    return players.map(p => ({ type: 'player', key: `p${p.id}`, player: p }));
  }
  const rows = [];
  groupPlayersByTee(players, p => ({ id: p.tee_id, name: p.tee_name })).forEach(section => {
    rows.push({
      type: 'tee',
      key: `t${section.key}`,
      teeName: section.tee?.name,
      // Any player off this tee can stand for the section's card.
      samplePlayerId: section.players[0].id,
    });
    section.players.forEach(p => rows.push({ type: 'player', key: `p${p.id}`, player: p }));
  });
  return rows;
};
